import { useState, useEffect, useCallback } from 'react';

// Versioned localStorage wrapper. Stores values as { v, data } so future
// schema changes can detect old shapes and either migrate or reset rather
// than blowing up on stale entries left in a user's browser.
//
// Phase-2 migration to a real backend will read from these keys to
// "claim" local data once an account exists, so the storage shape is a
// load-bearing contract — keep changes additive when possible.

const STORAGE_PREFIX = 'bmw.stack.';

function readRaw(key) {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw == null) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeRaw(key, value) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private mode / quota exceeded — fail silent; in-memory state still works */
  }
}

export default function useLocalState(key, defaultValue, version = 1) {
  const [value, setValue] = useState(() => {
    const stored = readRaw(key);
    if (!stored || stored.v !== version) return defaultValue;
    return stored.data;
  });

  // Persist on change. We intentionally do not debounce — typical writes
  // are infrequent (one per user interaction) and localStorage is sync,
  // but cheap for small payloads.
  useEffect(() => {
    writeRaw(key, { v: version, data: value });
  }, [key, version, value]);

  // Stable setter for use in callbacks
  const set = useCallback((next) => {
    setValue(prev => (typeof next === 'function' ? next(prev) : next));
  }, []);

  return [value, set];
}
